import { HTMLAttributes, PropsWithChildren, ReactNode } from 'react';
import { DialogContentProps } from '@radix-ui/react-dialog';
import { useTranslation } from '@sib/i18n';
import { cn } from '../utils';
import { Button, ButtonProps } from './Button';
import { Dialog, DialogClose, DialogContent } from './Dialog';

type ActionButtonProps = ButtonProps & { label?: ReactNode };

interface ConfirmationDialogProps extends PropsWithChildren {
  open?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: ReactNode;
  description?: ReactNode;
  loading?: boolean;
  DialogContentProps?: DialogContentProps;
  TitleProps?: HTMLAttributes<HTMLHeadingElement>;
  DescriptionProps?: HTMLAttributes<HTMLParagraphElement>;
  FooterProps?: HTMLAttributes<HTMLDivElement>;
  CancelButtonProps?: ActionButtonProps;
  ConfirmButtonProps?: ActionButtonProps;
}

const ConfirmationDialog = ({
  open = false,
  onClose,
  onConfirm,
  title,
  description,
  children,
  loading = false,
  DialogContentProps,
  TitleProps,
  DescriptionProps,
  FooterProps,
  CancelButtonProps = {},
  ConfirmButtonProps = {},
}: ConfirmationDialogProps) => {
  const { t } = useTranslation();
  const { label: cancelLabel, ...cancelButtonProps } = CancelButtonProps;
  const { label: confirmLabel, ...confirmButtonProps } = ConfirmButtonProps;

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent
        {...DialogContentProps}
        className={cn(
          'flex max-w-[95vw] flex-col items-center justify-center gap-7 rounded-lg bg-white p-7 text-center sm:max-w-[727px] sm:min-w-[520px]',
          DialogContentProps?.className
        )}
      >
        {title &&
          (typeof title === 'string' ? (
            <h4
              {...TitleProps}
              className={cn('text-primary', TitleProps?.className)}
            >
              {title}
            </h4>
          ) : (
            title
          ))}
        {description &&
          (typeof description === 'string' ? (
            <p
              {...DescriptionProps}
              className={cn(
                'text-primary text-sm',
                DescriptionProps?.className
              )}
            >
              {description}
            </p>
          ) : (
            description
          ))}
        {children}
        <div
          {...FooterProps}
          className={cn('flex justify-center gap-4', FooterProps?.className)}
        >
          <DialogClose asChild>
            <Button disabled={loading} {...cancelButtonProps} variant="outline">
              {cancelLabel ?? t('cancel')}
            </Button>
          </DialogClose>
          <Button {...confirmButtonProps} loading={loading} onClick={onConfirm}>
            {confirmLabel ?? t('confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { ConfirmationDialog };
export type { ConfirmationDialogProps, ActionButtonProps };
